import { watch } from 'vue';
import { useToken } from './useToken';

type EventType =
  | 'ready'
  | 'submitted'
  | 'error'
  | 'resize'
  | 'reprocessed'
  | 'instructionChanged';

interface KauntEvent {
  source: 'kaunt-component';
  type: EventType;
  component: string;
  sessionId?: string;
  payload?: Record<string, unknown>;
}

export function useParentMessaging(componentName: string) {
  const { token, getToken, getAllowedOrigins, sessionId } = useToken();

  const emit = (type: EventType, payload?: Record<string, unknown>) => {
    const message: KauntEvent = {
      source: 'kaunt-component',
      type,
      component: componentName,
      sessionId: sessionId.value ?? undefined,
      payload
    };

    if (window.parent === window) return;

    const targets = getAllowedOrigins();
    if (targets.length === 0) {
      console.warn('[Kaunt] Not emitting — no allowed origins available');
      return;
    }
    for (const origin of targets) {
      window.parent.postMessage(message, origin);
    }
  };

  const waitForToken = (): Promise<string> => new Promise((resolve, reject) => {
    const existing = getToken();
    if (existing) {
      resolve(existing);
      return;
    }
    const stop = watch(token, (value) => {
      if (value) {
        stop();
        window.clearTimeout(timeout);
        resolve(value);
      }
    });
    const timeout = window.setTimeout(() => {
      stop();
      reject(new Error('Token timeout'));
    }, 5000);
  });

  return { emit, waitForToken, sessionId };
}
